import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import type { MetaGraphFilter } from '../constants';
import {
  ACTIVE_CAMPAIGNS_FILTER,
  CAMPAIGN_ID_CHUNK_SIZE,
  EDGE_FIELDS,
  GRAPH_PAGE_LIMIT,
  HAS_META_CREDENTIALS,
  INSIGHTS_FIELDS,
  MAX_GRAPH_PAGES,
  META_ACCESS_TOKEN,
  META_AD_ACCOUNT_ID,
  META_GRAPH_BASE_URL,
} from '../constants';
import type { MetaAdStructure, MetaEntity, MetaInsightsRow } from '../types';

/**
 * Live Meta Graph API reads for the v2 selection tree.
 *
 * Uses `fakeBaseQuery` + `queryFn` rather than `customBaseQuery`: this talks to
 * graph.facebook.com, not our backend, so the app auth header and 401
 * auto-logout would be wrong here. It is NOT the banned
 * `fetchBaseQuery({ baseUrl })`-at-our-backend anti-pattern — every request is
 * issued explicitly below so the cursor pagination can be followed.
 *
 * See `../API.md` for the request/response contract, intended for the backend
 * team migrating these calls behind `/v1/data-analytics/meta/*`.
 */

type EdgeName = keyof typeof EDGE_FIELDS;

interface GraphPage {
  data?: MetaEntity[];
  paging?: { next?: string };
  error?: { message?: string };
}

/**
 * Follow `paging.next` until the edge is exhausted (or the page cap is hit) and
 * return every row. `paging.next` is a fully-formed URL that already carries the
 * cursor and token — never log it.
 */
const fetchAllPages = async (
  edge: EdgeName,
  filtering?: MetaGraphFilter[],
): Promise<MetaEntity[]> => {
  const params = new URLSearchParams({
    fields: EDGE_FIELDS[edge],
    limit: String(GRAPH_PAGE_LIMIT),
    access_token: META_ACCESS_TOKEN,
  });
  if (filtering?.length) params.set('filtering', JSON.stringify(filtering));

  let url: string | undefined =
    `${META_GRAPH_BASE_URL}/${META_AD_ACCOUNT_ID}/${edge}?${params.toString()}`;
  const rows: MetaEntity[] = [];

  for (let page = 0; page < MAX_GRAPH_PAGES && url; page += 1) {
    const response = await fetch(url);
    const body = (await response.json()) as GraphPage;

    // Graph reports failures in the body even on some non-2xx responses, so
    // check both. The message never contains the token.
    if (body?.error) {
      throw new Error(body.error.message ?? `Meta ${edge} request failed`);
    }
    if (!response.ok) {
      throw new Error(`Meta ${edge} request failed (${response.status})`);
    }

    rows.push(...(body.data ?? []));
    url = body.paging?.next;
  }

  return rows;
};

const chunk = <T>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size)
    out.push(items.slice(i, i + size));
  return out;
};

/**
 * Campaigns first, then their ad sets and ads.
 *
 * When `activeCampaignsOnly` is set (the default), the campaigns sweep is
 * filtered to ACTIVE and the two child sweeps are scoped to the ids it
 * returned — so we never download the thousands of objects hanging off paused
 * campaigns. Children are NOT status-filtered: a paused ad set inside a live
 * campaign is exactly what the red dot is for.
 */
const fetchAdStructure = async (
  activeCampaignsOnly: boolean,
): Promise<MetaAdStructure> => {
  const campaigns = await fetchAllPages(
    'campaigns',
    activeCampaignsOnly ? ACTIVE_CAMPAIGNS_FILTER : undefined,
  );

  if (!campaigns.length) return { campaigns: [], adsets: [], ads: [] };

  // One `campaign.id IN [...]` filter per id chunk; `undefined` means an
  // unscoped account-wide sweep (the "include paused campaigns" path).
  const childFilters: (MetaGraphFilter[] | undefined)[] = activeCampaignsOnly
    ? chunk(
        campaigns.map((campaign) => campaign.id),
        CAMPAIGN_ID_CHUNK_SIZE,
      ).map((ids) => [{ field: 'campaign.id', operator: 'IN', value: ids }])
    : [undefined];

  const [adsetChunks, adChunks] = await Promise.all([
    Promise.all(childFilters.map((f) => fetchAllPages('adsets', f))),
    Promise.all(childFilters.map((f) => fetchAllPages('ads', f))),
  ]);

  return {
    campaigns,
    adsets: adsetChunks.flat(),
    ads: adChunks.flat(),
  };
};

export interface MetaAdStructureArgs {
  /** Default true. False sweeps the whole account (far slower — see API.md). */
  activeCampaignsOnly: boolean;
}

export interface MetaWeeklyInsightsArgs {
  /** Scope filter from `buildScopeFilter`; `null` means the whole account. */
  filter: MetaGraphFilter[] | null;
  /**
   * `YYYY-MM-DD`, the first day of the earliest column — a Monday for weekly,
   * the 1st for monthly. Both matter: `time_increment=7` measures its buckets
   * from this date, and `monthly` truncates the first bucket if it starts
   * mid-month.
   */
  since: string;
  /** `YYYY-MM-DD`, inclusive. */
  until: string;
  /** `'7'` for ISO weeks, `'monthly'` for calendar months. */
  timeIncrement: string;
}

/**
 * Bucketed insights for the selected scope.
 *
 * `level=account` + a scope `filtering` spec makes **Meta** do the aggregation,
 * so CPM/CPC/CTR come back correctly weighted per bucket.
 *
 * `time_increment=7` buckets in 7-day blocks measured from `since` — pass a
 * Monday and the buckets are ISO weeks. `time_increment=monthly` ignores
 * `since` for alignment and snaps to calendar months instead, so `since` has to
 * be the 1st or the first bucket comes back truncated.
 */
const fetchWeeklyInsights = async ({
  filter,
  since,
  until,
  timeIncrement,
}: MetaWeeklyInsightsArgs): Promise<MetaInsightsRow[]> => {
  const params = new URLSearchParams({
    level: 'account',
    fields: INSIGHTS_FIELDS,
    time_range: JSON.stringify({ since, until }),
    time_increment: timeIncrement,
    limit: String(GRAPH_PAGE_LIMIT),
    access_token: META_ACCESS_TOKEN,
  });
  if (filter?.length) params.set('filtering', JSON.stringify(filter));

  const response = await fetch(
    `${META_GRAPH_BASE_URL}/${META_AD_ACCOUNT_ID}/insights?${params.toString()}`,
  );
  const body = (await response.json()) as {
    data?: MetaInsightsRow[];
    error?: { message?: string };
  };

  if (body?.error) {
    throw new Error(body.error.message ?? 'Meta insights request failed');
  }
  if (!response.ok) {
    throw new Error(`Meta insights request failed (${response.status})`);
  }
  return body.data ?? [];
};

export const metaGraphApi = createApi({
  reducerPath: 'metaGraphApi',
  baseQuery: fakeBaseQuery<string>(),
  endpoints: (builder) => ({
    getMetaAdStructure: builder.query<MetaAdStructure, MetaAdStructureArgs>({
      // The ad structure changes on the order of hours, not seconds — hold the
      // cache long enough that reopening the picker is instant.
      keepUnusedDataFor: 1800,
      queryFn: async ({ activeCampaignsOnly }) => {
        if (!HAS_META_CREDENTIALS) {
          return {
            error:
              'Meta credentials are not configured (VITE_META_ACCESS_TOKEN / VITE_META_AD_ACCOUNT_ID).',
          };
        }

        try {
          return { data: await fetchAdStructure(activeCampaignsOnly) };
        } catch (err) {
          return {
            error:
              err instanceof Error
                ? err.message
                : 'Failed to load the Meta ad structure',
          };
        }
      },
    }),

    getMetaWeeklyInsights: builder.query<
      MetaInsightsRow[],
      MetaWeeklyInsightsArgs
    >({
      // Closed periods never change; the one in progress moves slowly.
      keepUnusedDataFor: 600,
      queryFn: async (args) => {
        if (!HAS_META_CREDENTIALS) {
          return {
            error:
              'Meta credentials are not configured (VITE_META_ACCESS_TOKEN / VITE_META_AD_ACCOUNT_ID).',
          };
        }

        try {
          return { data: await fetchWeeklyInsights(args) };
        } catch (err) {
          return {
            error:
              err instanceof Error
                ? err.message
                : 'Failed to load Meta insights',
          };
        }
      },
    }),
  }),
});

export const { useGetMetaAdStructureQuery, useGetMetaWeeklyInsightsQuery } =
  metaGraphApi;
